import React, { useState, useEffect, useCallback, useRef, useMemo, useReducer } from "react";
import { Link } from "react-router-dom";
import "./AdminGallery.css";

// =================== CONSTANTES ===================
const SUPPORTED_FORMATS = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_STORAGE_SIZE = 5 * 1024 * 1024; // 5MB
const NOTIFICATION_DURATION = 4000;
const SEARCH_DEBOUNCE = 300;

// =================== UTILITAIRES ===================
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Aujourd\'hui';
  if (diffDays === 1) return 'Hier';
  if (diffDays < 7) return `Il y a ${diffDays} jours`;
  
  return date.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const validateImageFile = (file) => {
  if (!SUPPORTED_FORMATS.includes(file.type)) {
    throw new Error(`Format non supporté: ${file.type}`);
  }
  
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`Fichier trop volumineux: ${(file.size / 1024 / 1024).toFixed(2)}MB (max: 10MB)`);
  }
  
  return true;
};

const generateUniqueFileName = (originalName) => {
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 8);
  const extension = originalName.split('.').pop()?.toLowerCase() || 'jpg';
  return `img_${timestamp}_${randomStr}.${extension}`;
};

// =================== REDUCERS ===================
const initialGalleryState = {
  images: [],
  favorites: new Set(),
  selectedImages: new Set(),
  tags: new Set(),
  selectedTags: new Set(),
  loading: true,
  isUploading: false,
  uploadProgress: 0,
  dragActive: false,
};

const galleryReducer = (state, action) => {
  switch (action.type) {
    case 'SET_IMAGES':
      return { ...state, images: action.payload };
    
    case 'ADD_IMAGES':
      return { ...state, images: [...state.images, ...action.payload] };
    
    case 'DELETE_IMAGE':
      return {
        ...state,
        images: state.images.filter(img => img.id !== action.payload),
        selectedImages: new Set([...state.selectedImages].filter(id => id !== action.payload)),
        favorites: new Set([...state.favorites].filter(id => id !== action.payload))
      };
    
    case 'DELETE_SELECTED':
      const remainingImages = state.images.filter(img => !state.selectedImages.has(img.id));
      return {
        ...state,
        images: remainingImages,
        selectedImages: new Set(),
        favorites: new Set([...state.favorites].filter(id => !state.selectedImages.has(id)))
      };
    
    case 'SET_FAVORITES':
      return { ...state, favorites: new Set(action.payload) };
    
    case 'TOGGLE_FAVORITE':
      const newFavorites = new Set(state.favorites);
      if (newFavorites.has(action.payload)) {
        newFavorites.delete(action.payload);
      } else {
        newFavorites.add(action.payload);
      }
      return { ...state, favorites: newFavorites };
    
    case 'SET_SELECTED_IMAGES':
      return { ...state, selectedImages: new Set(action.payload) };
    
    case 'TOGGLE_IMAGE_SELECTION':
      const newSelection = new Set(state.selectedImages);
      if (newSelection.has(action.payload)) {
        newSelection.delete(action.payload);
      } else {
        newSelection.add(action.payload);
      }
      return { ...state, selectedImages: newSelection };
    
    case 'SET_TAGS':
      return { ...state, tags: new Set(action.payload) };
    
    case 'SET_SELECTED_TAGS':
      return { ...state, selectedTags: new Set(action.payload) };
    
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_UPLOADING':
      return { ...state, isUploading: action.payload };
    
    case 'SET_UPLOAD_PROGRESS':
      return { ...state, uploadProgress: action.payload };
    
    case 'SET_DRAG_ACTIVE':
      return { ...state, dragActive: action.payload };
    
    default:
      return state;
  }
};

const initialUIState = {
  viewMode: 'grid',
  sortBy: 'date',
  sortOrder: 'desc',
  filterText: '',
  showOnlyFavorites: false,
  sizeFilter: 'all',
  dateFilter: 'all',
  gridSize: 'medium',
  showFilters: false,
  showSearch: false,
  bulkActionsOpen: false,
  showDeleteModal: false,
  showDeleteNonFavoritesModal: false,
  showImagePreview: false,
  previewImage: null,
  previewIndex: 0,
  imageToDelete: null,
  notification: null,
  searchSuggestions: []
};

const uiReducer = (state, action) => {
  switch (action.type) {
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.payload };
    
    case 'SET_SORT':
      return { ...state, sortBy: action.payload.sortBy, sortOrder: action.payload.sortOrder };
    
    case 'SET_FILTER_TEXT':
      return { ...state, filterText: action.payload };
    
    case 'SET_SHOW_ONLY_FAVORITES':
      return { ...state, showOnlyFavorites: action.payload };
    
    case 'SET_SIZE_FILTER':
      return { ...state, sizeFilter: action.payload };
    
    case 'SET_DATE_FILTER':
      return { ...state, dateFilter: action.payload };
    
    case 'SET_GRID_SIZE':
      return { ...state, gridSize: action.payload };
    
    case 'TOGGLE_FILTERS':
      return { ...state, showFilters: !state.showFilters };
    
    case 'SET_NOTIFICATION':
      return { ...state, notification: action.payload };
    
    case 'SET_SEARCH_SUGGESTIONS':
      return { ...state, searchSuggestions: action.payload };
    
    case 'SET_PREVIEW':
      return { 
        ...state, 
        showImagePreview: action.payload.show,
        previewImage: action.payload.image,
        previewIndex: action.payload.index
      };
    
    case 'SET_MODAL':
      return { ...state, [action.payload.modal]: action.payload.show, imageToDelete: action.payload.image };
    
    case 'RESET_FILTERS':
      return {
        ...state,
        filterText: '',
        showOnlyFavorites: false,
        sizeFilter: 'all',
        dateFilter: 'all'
      };
    
    default:
      return state;
  }
};

// =================== HOOKS PERSONNALISÉS ===================
const useLocalStorage = () => {
  const saveImages = useCallback((images) => {
    try {
      const dataSize = JSON.stringify(images).length;
      if (dataSize > MAX_STORAGE_SIZE) {
        console.warn('Storage size warning:', dataSize);
      }
      localStorage.setItem('adminGallery', JSON.stringify(images));
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        throw new Error('Espace de stockage insuffisant');
      }
      throw error;
    }
  }, []);

  const loadImages = useCallback(() => {
    try {
      const stored = localStorage.getItem('adminGallery');
      return stored ? JSON.parse(stored).filter(img => 
        img && img.id && img.dataUrl && img.originalName
      ) : [];
    } catch (error) {
      console.error('Error loading images:', error);
      return [];
    }
  }, []);

  const saveFavorites = useCallback((favorites) => {
    try {
      localStorage.setItem('adminGalleryFavorites', JSON.stringify(Array.from(favorites)));
    } catch (error) {
      console.error('Error saving favorites:', error);
    }
  }, []);

  const loadFavorites = useCallback(() => {
    try {
      const stored = localStorage.getItem('adminGalleryFavorites');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch (error) {
      console.error('Error loading favorites:', error);
      return new Set();
    }
  }, []);

  const saveSettings = useCallback((settings) => {
    try {
      localStorage.setItem('adminGallerySettings', JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  }, []);

  const loadSettings = useCallback(() => {
    try {
      const stored = localStorage.getItem('adminGallerySettings');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error loading settings:', error);
      return null;
    }
  }, []);

  return { saveImages, loadImages, saveFavorites, loadFavorites, saveSettings, loadSettings };
};

const useNotification = () => {
  const [notification, setNotification] = useState(null);
  const timeoutRef = useRef(null);

  const showNotification = useCallback((message, type = 'success', duration = NOTIFICATION_DURATION) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    setNotification({ message, type, id: Date.now() });
    
    timeoutRef.current = setTimeout(() => {
      setNotification(null);
    }, duration);
  }, []);

  const hideNotification = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setNotification(null);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { notification, showNotification, hideNotification };
};

const useImageProcessing = () => {
  const fileToDataUrl = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  const getImageDimensions = useCallback((dataUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve({ width: 0, height: 0 });
      img.src = dataUrl;
    });
  }, []);

  const processImageFile = useCallback(async (file, index = 0) => {
    validateImageFile(file);
    
    const dataUrl = await fileToDataUrl(file);
    const uniqueName = generateUniqueFileName(file.name);
    const dimensions = await getImageDimensions(dataUrl);
    
    return {
      id: `img_${Date.now()}_${index}_${Math.random().toString(36).substring(2, 8)}`,
      name: uniqueName,
      originalName: file.name,
      dataUrl: dataUrl,
      size: file.size,
      type: file.type,
      createdAt: new Date().toISOString(),
      dimensions: dimensions,
      tags: [],
      description: '',
      metadata: {
        lastModified: file.lastModified ? new Date(file.lastModified).toISOString() : null,
        aspectRatio: dimensions.width / dimensions.height,
        megapixels: (dimensions.width * dimensions.height / 1000000).toFixed(1)
      }
    };
  }, [fileToDataUrl, getImageDimensions]);

  return { processImageFile };
};

// =================== COMPOSANT PRINCIPAL ===================
function AdminGallery() {
  const [galleryState, galleryDispatch] = useReducer(galleryReducer, initialGalleryState);
  const [uiState, uiDispatch] = useReducer(uiReducer, initialUIState);
  
  const { saveImages, loadImages, saveFavorites, loadFavorites, saveSettings, loadSettings } = useLocalStorage();
  const { notification, showNotification, hideNotification } = useNotification();
  const { processImageFile } = useImageProcessing();
  
  const fileInputRef = useRef(null);
  const searchRef = useRef(null);
  const gridRef = useRef(null);

  // =================== FILTRAGE ET TRI OPTIMISÉS ===================
  const filteredAndSortedImages = useMemo(() => {
    let filtered = galleryState.images;

    // Filtres
    if (uiState.showOnlyFavorites) {
      filtered = filtered.filter(img => galleryState.favorites.has(img.id));
    }

    if (galleryState.selectedTags.size > 0) {
      filtered = filtered.filter(img => 
        img.tags && img.tags.some(tag => galleryState.selectedTags.has(tag))
      );
    }

    if (uiState.sizeFilter !== 'all') {
      filtered = filtered.filter(img => {
        const sizeInMB = img.size / (1024 * 1024);
        switch (uiState.sizeFilter) {
          case 'small': return sizeInMB < 1;
          case 'medium': return sizeInMB >= 1 && sizeInMB < 5;
          case 'large': return sizeInMB >= 5;
          default: return true;
        }
      });
    }

    if (uiState.dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(img => {
        const imgDate = new Date(img.createdAt);
        switch (uiState.dateFilter) {
          case 'today':
            return imgDate.toDateString() === now.toDateString();
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return imgDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            return imgDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    if (uiState.filterText) {
      const searchTerm = uiState.filterText.toLowerCase();
      filtered = filtered.filter(img => 
        img.originalName.toLowerCase().includes(searchTerm) ||
        img.name.toLowerCase().includes(searchTerm) ||
        (img.tags && img.tags.some(tag => tag.toLowerCase().includes(searchTerm))) ||
        (img.description && img.description.toLowerCase().includes(searchTerm))
      );
    }

    // Tri
    return filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (uiState.sortBy) {
        case 'name':
          comparison = a.originalName.localeCompare(b.originalName);
          break;
        case 'size':
          comparison = a.size - b.size;
          break;
        case 'dimensions':
          comparison = (a.dimensions.width * a.dimensions.height) - (b.dimensions.width * b.dimensions.height);
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
        case 'date':
        default:
          comparison = new Date(a.createdAt) - new Date(b.createdAt);
          break;
      }
      
      return uiState.sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [
    galleryState.images, 
    galleryState.favorites, 
    galleryState.selectedTags,
    uiState.filterText, 
    uiState.sortBy, 
    uiState.sortOrder, 
    uiState.showOnlyFavorites, 
    uiState.sizeFilter, 
    uiState.dateFilter
  ]);

  // =================== GESTIONNAIRES D'ÉVÉNEMENTS ===================
  const handleFileUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    galleryDispatch({ type: 'SET_UPLOADING', payload: true });
    galleryDispatch({ type: 'SET_UPLOAD_PROGRESS', payload: 0 });
    
    const newImages = [];
    const errors = [];
    const totalFiles = files.length;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        const imageData = await processImageFile(file, i);
        newImages.push(imageData);
        galleryDispatch({ type: 'SET_UPLOAD_PROGRESS', payload: Math.round(((i + 1) / totalFiles) * 100) });
      } catch (error) {
        errors.push(`${file.name}: ${error.message}`);
      }
    }

    if (newImages.length > 0) {
      galleryDispatch({ type: 'ADD_IMAGES', payload: newImages });
      try {
        saveImages([...galleryState.images, ...newImages]);
        showNotification(`${newImages.length} image(s) ajoutée(s) avec succès`, 'success');
      } catch (error) {
        showNotification(error.message, 'error');
      }
    }

    if (errors.length > 0) {
      showNotification(
        `${errors.length} erreur(s): ${errors[0]}${errors.length > 1 ? ' (+' + (errors.length - 1) + ' autres)' : ''}`, 
        'warning', 
        6000
      );
    }

    galleryDispatch({ type: 'SET_UPLOADING', payload: false });
    galleryDispatch({ type: 'SET_UPLOAD_PROGRESS', payload: 0 });
  }, [galleryState.images, processImageFile, saveImages, showNotification]);

  const toggleFavorite = useCallback((imageId) => {
    galleryDispatch({ type: 'TOGGLE_FAVORITE', payload: imageId });
    const newFavorites = new Set(galleryState.favorites);
    
    if (newFavorites.has(imageId)) {
      newFavorites.delete(imageId);
      showNotification('Retiré des favoris', 'info', 2000);
    } else {
      newFavorites.add(imageId);
      showNotification('Ajouté aux favoris', 'success', 2000);
    }
    
    saveFavorites(newFavorites);
  }, [galleryState.favorites, saveFavorites, showNotification]);

  const deleteImage = useCallback((imageId) => {
    galleryDispatch({ type: 'DELETE_IMAGE', payload: imageId });
    const updatedImages = galleryState.images.filter(img => img.id !== imageId);
    saveImages(updatedImages);
    
    const newFavorites = new Set(galleryState.favorites);
    newFavorites.delete(imageId);
    saveFavorites(newFavorites);
    
    uiDispatch({ type: 'SET_MODAL', payload: { modal: 'showDeleteModal', show: false, image: null } });
    showNotification('Image supprimée', 'success', 2000);
  }, [galleryState.images, galleryState.favorites, saveImages, saveFavorites, showNotification]);

  // =================== GESTION DU GLISSER-DÉPOSER ===================
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      galleryDispatch({ type: 'SET_DRAG_ACTIVE', payload: true });
    } else if (e.type === "dragleave") {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        galleryDispatch({ type: 'SET_DRAG_ACTIVE', payload: false });
      }
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    galleryDispatch({ type: 'SET_DRAG_ACTIVE', payload: false });
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  }, [handleFileUpload]);

  // =================== EFFETS ===================
  useEffect(() => {
    const loadData = async () => {
      galleryDispatch({ type: 'SET_LOADING', payload: true });
      
      const images = loadImages();
      const favorites = loadFavorites();
      const settings = loadSettings();
      
      galleryDispatch({ type: 'SET_IMAGES', payload: images });
      galleryDispatch({ type: 'SET_FAVORITES', payload: favorites });
      
      // Extraire les tags
      const allTags = new Set();
      images.forEach(img => {
        if (img.tags && Array.isArray(img.tags)) {
          img.tags.forEach(tag => allTags.add(tag));
        }
      });
      galleryDispatch({ type: 'SET_TAGS', payload: allTags });
      
      // Charger les paramètres
      if (settings) {
        if (settings.viewMode) uiDispatch({ type: 'SET_VIEW_MODE', payload: settings.viewMode });
        if (settings.sortBy) uiDispatch({ type: 'SET_SORT', payload: { sortBy: settings.sortBy, sortOrder: settings.sortOrder || 'desc' } });
        if (settings.gridSize) uiDispatch({ type: 'SET_GRID_SIZE', payload: settings.gridSize });
        if (settings.showFilters !== undefined) uiDispatch({ type: 'TOGGLE_FILTERS' });
      }
      
      galleryDispatch({ type: 'SET_LOADING', payload: false });
    };
    
    loadData();
  }, [loadImages, loadFavorites, loadSettings]);

  // Auto-save des paramètres
  useEffect(() => {
    const settings = {
      viewMode: uiState.viewMode,
      sortBy: uiState.sortBy,
      sortOrder: uiState.sortOrder,
      gridSize: uiState.gridSize,
      showFilters: uiState.showFilters
    };
    saveSettings(settings);
  }, [uiState.viewMode, uiState.sortBy, uiState.sortOrder, uiState.gridSize, uiState.showFilters, saveSettings]);

  // Statistiques calculées
  const stats = useMemo(() => {
    const totalSize = galleryState.images.reduce((sum, img) => sum + img.size, 0);
    return {
      totalSize: formatFileSize(totalSize),
      avgSize: galleryState.images.length > 0 ? formatFileSize(totalSize / galleryState.images.length) : '0 B'
    };
  }, [galleryState.images]);

  if (galleryState.loading) {
    return (
      <div className="admin-gallery loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Chargement de la galerie...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-gallery">
      {/* Notification */}
      {notification && (
        <div className={`notification notification-${notification.type} show`}>
          <div className="notification-content">
            <div className="notification-icon">
              <i className={`bi bi-${
                notification.type === 'success' ? 'check-circle-fill' : 
                notification.type === 'error' ? 'exclamation-triangle-fill' : 
                notification.type === 'warning' ? 'exclamation-triangle-fill' :
                'info-circle-fill'
              }`}></i>
            </div>
            <div className="notification-text">
              <span>{notification.message}</span>
            </div>
            <button 
              className="notification-close"
              onClick={hideNotification}
              aria-label="Fermer la notification"
            >
              <i className="bi bi-x"></i>
            </button>
          </div>
        </div>
      )}

      {/* En-tête */}
      <div className="gallery-header">
        <div className="header-left">
          <h1 className="gallery-title">
            <i className="bi bi-images me-3"></i>
            Galerie d'images
          </h1>
          <div className="gallery-stats">
            <div className="stat-item primary">
              <span className="stat-number">{filteredAndSortedImages.length}</span>
              <span className="stat-label">image{filteredAndSortedImages.length !== 1 ? 's' : ''}</span>
              <span className="stat-sublabel">sur {galleryState.images.length}</span>
            </div>
            {galleryState.selectedImages.size > 0 && (
              <div className="stat-item selected">
                <span className="stat-number">{galleryState.selectedImages.size}</span>
                <span className="stat-label">sélectionnée{galleryState.selectedImages.size !== 1 ? 's' : ''}</span>
              </div>
            )}
            {galleryState.favorites.size > 0 && (
              <div className="stat-item favorite">
                <i className="bi bi-heart-fill"></i>
                <span className="stat-number">{galleryState.favorites.size}</span>
                <span className="stat-label">favori{galleryState.favorites.size !== 1 ? 's' : ''}</span>
              </div>
            )}
            <div className="stat-item storage">
              <i className="bi bi-hdd"></i>
              <span className="stat-number">{stats.totalSize}</span>
              <span className="stat-label">total</span>
            </div>
          </div>
        </div>
        
        <div className="header-actions">
          <Link to="/admin/image-editor" className="btn btn-primary">
            <i className="bi bi-magic me-2"></i>
            Éditeur
          </Link>
          <Link to="/admin" className="btn btn-secondary">
            <i className="bi bi-arrow-left me-2"></i>
            Retour
          </Link>
        </div>
      </div>

      {/* Barre d'outils */}
      <div className="gallery-toolbar">
        <div className="toolbar-section">
          <div className="upload-section">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileUpload(e.target.files)}
              className="d-none"
            />
            <button
              className="btn btn-success"
              onClick={() => fileInputRef.current?.click()}
              disabled={galleryState.isUploading}
            >
              <i className="bi bi-cloud-upload me-2"></i>
              {galleryState.isUploading ? `Upload... ${galleryState.uploadProgress}%` : 'Uploader'}
            </button>
            {galleryState.isUploading && (
              <div className="upload-progress">
                <div className="progress-bar" style={{ width: `${galleryState.uploadProgress}%` }}></div>
                <span className="progress-text">{galleryState.uploadProgress}%</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Zone de drop */}
      {galleryState.dragActive && (
        <div
          className="drop-overlay active"
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="drop-message">
            <div className="drop-icon">
              <i className="bi bi-cloud-upload display-1"></i>
            </div>
            <h3>Déposez vos images ici</h3>
            <p>Formats supportés: JPEG, PNG, GIF, WebP, BMP</p>
            <p className="drop-hint">Jusqu'à 10MB par fichier</p>
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <div
        className="gallery-content"
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {filteredAndSortedImages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <i className="bi bi-images display-1"></i>
            </div>
            <h3>Aucune image dans la galerie</h3>
            <p className="empty-description">
              Commencez par uploader des images ou glissez-déposez des fichiers ici.
            </p>
            <div className="empty-actions">
              <button
                className="btn btn-primary btn-lg"
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="bi bi-cloud-upload me-2"></i>
                Uploader vos premières images
              </button>
            </div>
          </div>
        ) : (
          <div 
            ref={gridRef}
            className={`images-container ${uiState.viewMode} grid-${uiState.gridSize}`}
          >
            {filteredAndSortedImages.map((image, index) => (
              <div
                key={image.id}
                className={`image-item ${galleryState.selectedImages.has(image.id) ? 'selected' : ''} ${galleryState.favorites.has(image.id) ? 'favorite' : ''}`}
              >
                <div className="favorite-button">
                  <button
                    className={`btn btn-sm ${galleryState.favorites.has(image.id) ? 'btn-danger' : 'btn-outline-light'}`}
                    onClick={() => toggleFavorite(image.id)}
                    title={galleryState.favorites.has(image.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                  >
                    <i className={`bi ${galleryState.favorites.has(image.id) ? 'bi-heart-fill' : 'bi-heart'}`}></i>
                  </button>
                </div>

                <div className="image-preview">
                  <img
                    src={image.dataUrl}
                    alt={image.originalName}
                    loading="lazy"
                  />
                  <div className="image-overlay">
                    <div className="overlay-actions">
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => {
                          uiDispatch({ 
                            type: 'SET_MODAL', 
                            payload: { modal: 'showDeleteModal', show: true, image: image } 
                          });
                        }}
                        title="Supprimer"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="image-info">
                  <h6 className="image-name" title={image.originalName}>
                    {image.originalName}
                  </h6>
                  <div className="image-details">
                    <span className="image-size">{formatFileSize(image.size)}</span>
                    <span className="image-dimensions">
                      {image.dimensions.width} × {image.dimensions.height}
                    </span>
                    <span className="image-date">{formatDate(image.createdAt)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de suppression */}
      {uiState.showDeleteModal && uiState.imageToDelete && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                  Confirmer la suppression
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => uiDispatch({ 
                    type: 'SET_MODAL', 
                    payload: { modal: 'showDeleteModal', show: false, image: null } 
                  })}
                ></button>
              </div>
              <div className="modal-body">
                <p>Êtes-vous sûr de vouloir supprimer cette image définitivement ?</p>
                <div className="delete-preview">
                  <img
                    src={uiState.imageToDelete.dataUrl}
                    alt={uiState.imageToDelete.originalName}
                    className="img-thumbnail"
                  />
                  <div className="delete-info">
                    <strong>{uiState.imageToDelete.originalName}</strong>
                    <div className="delete-details">
                      <span>{formatFileSize(uiState.imageToDelete.size)}</span>
                      <span>{uiState.imageToDelete.dimensions.width} × {uiState.imageToDelete.dimensions.height}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => uiDispatch({ 
                    type: 'SET_MODAL', 
                    payload: { modal: 'showDeleteModal', show: false, image: null } 
                  })}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => deleteImage(uiState.imageToDelete.id)}
                >
                  <i className="bi bi-trash me-2"></i>
                  Supprimer définitivement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop des modales */}
      {uiState.showDeleteModal && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
}

export default AdminGallery;